"use client";
/**
 * This file is a collection of custom styled components to be rendered for <RichText> and <MDXRemote> content.
 */
import { Link, type Link_T } from "@areahub/ui/components/link/link";
import {
  Alert,
  Box,
  chakra,
  Kbd,
  Link as ChakraLink,
  OmitCommonProps,
  Text,
  TextProps,
  type ChakraProps,
} from "@chakra-ui/react";
import { LinkRendererProps } from "@graphcms/rich-text-react-renderer";
import Image from "next/legacy/image";
import { DetailedHTMLProps, HTMLAttributes, Suspense, useContext } from "react";
import { TextAlign } from "sharp";
import slugify from "slugify";

const Pre = (props: any) => (
  <chakra.div my="2em" borderRadius="sm" {...props} />
);

const Table = (props: any) => (
  <chakra.div overflowX="auto">
    <chakra.table textAlign="left" mt="32px" width="full" {...props} />
  </chakra.div>
);

const THead = (props: any) => (
  <chakra.th fontWeight="semibold" p={2} fontSize="sm" {...props} />
);

const THeadRow = (props: any) => (
  <chakra.thead fontWeight="semibold" p={2} fontSize="sm" {...props} />
);

const TData = (props: any) => (
  <chakra.td
    p={2}
    borderTopWidth="1px"
    borderColor="inherit"
    fontSize="sm"
    whiteSpace="normal"
    {...props}
  />
);

const getHeadingId = (props: any): string => {
  if (props?.id) {
    return props?.id;
  }
  if (props?.children) {
    if (props?.children?.props?.content?.[0]?.text) {
      return slugify(props?.children?.props?.content?.[0]?.text, {
        lower: true,
      });
    }
    if (typeof props?.children === "string") {
      return slugify(props?.children, { lower: true });
    }
  }
  return "";
};

export const LinkedHeading = (props: any) => {
  const id = getHeadingId(props);
  return (
    <chakra.h2
      id={id}
      data-group=""
      css={{ scrollMarginBlock: "6.875rem" }}
      {...props}
    >
      <span className="content">{props?.children}</span>
      <chakra.a
        aria-label="anchor"
        fontWeight="normal"
        outline="none"
        _focus={{ opacity: 1, boxShadow: "outline" }}
        _hover={{ textDecoration: "none" }}
        opacity={0}
        _groupHover={{ opacity: 1 }}
        ml="0.375rem"
        href={`#${id}`}
        color="inherit"
      >
        #
      </chakra.a>
    </chakra.h2>
  );
};

export const common = {
  h1: (props: any) => <LinkedHeading apply="styles.mdx.h1" mt="0" {...props} />,
  h2: (props: any) => <LinkedHeading apply="styles.mdx.h2" mt="0" {...props} />,
  h3: (props: any) => (
    <LinkedHeading as="h3" apply="styles.mdx.h3" mt="0" {...props} />
  ),
  h4: (props: any) => (
    <LinkedHeading as="h4" apply="styles.mdx.h4" mt="0" {...props} />
  ),
  h5: (props: any) => <LinkedHeading as="h5" mt="0" {...props} />,
  h6: (props: any) => <LinkedHeading as="h6" mt="0" {...props} />,
  hr: (props: any) => <chakra.hr apply="styles.mdx.hr" {...props} />,
  strong: (props: any) => (
    <Text as="strong" fontWeight="semibold" {...(props as TextProps)} />
  ),
  pre: Pre,
  img: ({ src, altText, height, width }: any) =>
    src ? (
      <Image
        src={src}
        alt={altText}
        height={height}
        width={width}
        style={{ objectFit: "cover" }}
        loading="lazy"
        quality={100}
      />
    ) : (
      <div />
    ),
  kbd: Kbd,
  br: ({ reset, ...props }: any) => (
    <Box
      as={reset ? "br" : undefined}
      height={reset ? undefined : "24px"}
      {...props}
    />
  ),
  table: Table,
  th: THead,
  td: TData,
  a: (props: any) => (
    <Link
      color="blue.500"
      _hover={{
        color: "blue.600",
        textDecoration: "underline",
      }}
      apply="styles.mdx.a"
      {...(props as Link_T)}
    />
  ),
  em: (props: any) => <chakra.em {...props} />,
  i: (props: any) => <Text as="i" {...props} />,
  p: (props: any) => <chakra.p apply="styles.mdx.p" {...props} />,
  ul: (props: any) => <chakra.ul apply="styles.mdx.ul" mt="0" {...props} />,
  ol: (props: any) => <chakra.ol apply="styles.mdx.ul" mt="0" {...props} />,
  li: (props: any) => <chakra.li pb="4px" {...props} />,
  blockquote: (props: any) => (
    <Alert
      as="blockquote"
      mt="4"
      role="none"
      status="warning"
      variant="left-accent"
      rounded="4px"
      my="1.5rem"
      {...props}
    />
  ),
};
export const richTextRenderers = {
  ...common,
  bold: (props: any) => (
    <Text as="strong" fontWeight="semibold" {...(props as TextProps)} />
  ),
  a: ({ title, openInNewTab, ...props }: LinkRendererProps) => (
    <Link
      color="blue.500"
      _hover={{
        color: "blue.600",
        textDecoration: "underline",
      }}
      apply="styles.mdx.a"
      aria-label={
        (title ?? typeof props?.children === "string")
          ? (props?.children as string)
          : "AreaHub Link"
      }
      isExternal={openInNewTab ?? false}
      {...(props as Link_T)}
      target={openInNewTab ? "_blank" : undefined}
      rel={props?.rel ?? "noopener noreferrer"}
    />
  ),
  table_head: THeadRow,
  table_header_cell: THead,
  table_cell: TData,
  italic: (
    props: JSX.IntrinsicAttributes &
      OmitCommonProps<
        DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>,
        keyof ChakraProps
      > &
      ChakraProps & { as?: "em" | undefined },
  ) => <chakra.em {...props} />,
  underline: (
    props: JSX.IntrinsicAttributes &
      OmitCommonProps<
        DetailedHTMLProps<
          HTMLAttributes<HTMLParagraphElement>,
          HTMLParagraphElement
        >,
        keyof ChakraProps
      > &
      ChakraProps & { as?: "p" | undefined },
  ) => <chakra.p apply="styles.mdx.p" textDecoration="underline" {...props} />,
};

export const components: import("mdx/types").MDXComponents = {
  ...common,
  img: (props: any) => <Image {...props} loading="lazy" quality={100} />,
  Link: (props: any) => <Link {...props} />,
  Image: (props: any) => <Image {...props} loading="lazy" quality={100} />,
};

export const blockComponents = {
  ...components,
  h3: (props: any) => (
    <chakra.h3
      as="h3"
      apply="styles.mdx.h3"
      mt="0"
      {...props}
      fontWeight="medium"
    />
  ),
  h4: (props: any) => (
    <chakra.h4
      as="h4"
      apply="styles.mdx.h4"
      mt="0"
      {...props}
      fontWeight="medium"
    />
  ),
  h5: (props: any) => (
    <chakra.h5 as="h5" mt="0" {...props} fontWeight="medium" />
  ),
  h6: (props: any) => (
    <chakra.h6 as="h6" mt="0" {...props} fontWeight="medium" />
  ),
};
export const blockRenderers = {
  ...common,
  h3: (props: any) => (
    <chakra.h3 as="h3" apply="styles.mdx.h3" mt="0" {...props} />
  ),
  h4: (props: any) => (
    <chakra.h4 as="h4" apply="styles.mdx.h4" mt="0" {...props} />
  ),
  h5: (props: any) => <chakra.h5 as="h5" mt="0" {...props} />,
  h6: (props: any) => <chakra.h6 as="h6" mt="0" {...props} />,
  bold: (props: any) => (
    <Text as="strong" fontWeight="semibold" {...(props as TextProps)} />
  ),
  a: ({ title, openInNewTab, ...props }: LinkRendererProps) => (
    <Link
      color="blue.500"
      _hover={{
        color: "blue.600",
        textDecoration: "underline",
      }}
      apply="styles.mdx.a"
      aria-label={
        (title ?? typeof props?.children === "string")
          ? (props?.children as string)
          : "AreaHub Link"
      }
      isExternal={openInNewTab ?? false}
      {...(props as Link_T)}
      target={openInNewTab ? "_blank" : undefined}
      rel={props?.rel ?? "noopener noreferrer"}
    />
  ),
  table_head: THeadRow,
  table_header_cell: THead,
  table_cell: TData,
  italic: (props) => <chakra.em {...props} />,
  underline: (props) => (
    <chakra.p apply="styles.mdx.p" textDecoration="underline" {...props} />
  ),
};

export const customRichText = (
  color?: string,
  pt?: number,
  pb?: number,
  fontSize?: any,
  textAlign?: TextAlign,
) => ({
  ...richTextRenderers,
  p: (props: any) => (
    <chakra.p
      apply="styles.mdx.p"
      color={color}
      pt={pt}
      pb={pb}
      fontSize={
        fontSize ?? {
          base: 14,
          md: 16,
          lg: 17,
          xl: 18,
        }
      }
      textAlign={textAlign}
      {...props}
    />
  ),
  ul: (props: any) => (
    <chakra.ul apply="styles.mdx.ul" color={color} {...props} />
  ),
  h3: (props: any) => (
    <chakra.h3 as="h3" apply="styles.mdx.h3" mt="0" {...props} />
  ),
  h6: (props: any) => (
    <chakra.h6
      fontSize={{
        base: "16px",
        md: "18px",
        lg: "19px",
        xl: "20px",
      }}
      color={color}
      {...props}
    />
  ),
});

export default components;
